import {
  Autocomplete,
  Grid2,
  TextField,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Drawer,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Home } from "@mui/icons-material";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import {
  DropDownOptions,
  GroupInfo,
  MapResponseToDropdown,
  SitesInfo,
} from "../../Models/Models";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { mapResponseToDropdown } from "../../Utils/CommonFunctions";
import { FetchSiteGroups, FetchSites } from "../../ApiServices/ApiRequests";
import Loading from "../../Controller/LoadingController/Loading";
import { AppRoutes } from "../../Routes/Layout/Routes";
import React from "react";
import {
  MasterDataContextType,
  useMasterData,
} from "../../ContextAPI/MasterContext";

const HeaderComponent: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean).slice(1);
  const { masterState, dispatchMaster } = useMasterData();

  const handleInputChange = useCallback(
    (
      field: keyof MasterDataContextType,
      value:
        | string
        | boolean
        | DropDownOptions[]
        | { lat: number; lng: number }
        | dayjs.Dayjs
        | null
    ) => {
      dispatchMaster({ type: "SET_FIELD", field, value });
    },
    [dispatchMaster]
  );
  const user = JSON.parse(sessionStorage.getItem("LoginDetails") || "{}");
  useEffect(() => {
    fetchSitesData(user?.organization[0]?.orgCode);
  }, []);
  useEffect(() => {
    if (masterState.selectedSiteCode.length > 0) {
      fetchGroupsData(masterState.selectedSiteCode);
    }
  }, [masterState.selectedSiteCode]);

  const fetchSitesData = async (orgCode: string): Promise<void> => {
    try {
      handleInputChange("isloading", true);
      const data: SitesInfo[] = await FetchSites(orgCode);
      const sitesOptions: MapResponseToDropdown[] = mapResponseToDropdown(
        data,
        "siteName",
        "siteCode",
        "latitude",
        "longitude"
      );
      handleInputChange("siteOptions", sitesOptions);
      if (sitesOptions.length > 0) {
        if (masterState?.selectedSiteCode?.length == 0) {
          handleInputChange("selectedSiteCode", sitesOptions[0]?.value);
        }

        handleInputChange("binPlottingmapCenter", {
          lat: parseFloat(sitesOptions[0]?.extraField1 ?? "0"),
          lng: parseFloat(sitesOptions[0]?.extraField2 ?? "0"),
        });
      }
    } catch (error) {
      console.error("Error loading site Options data", error);
    } finally {
      handleInputChange("isloading", false);
    }
  };

  const fetchGroupsData = async (siteCode: string) => {
    try {
      handleInputChange("isloading", true);
      const data: GroupInfo[] = await FetchSiteGroups(siteCode);

      const groupOptions: MapResponseToDropdown[] = mapResponseToDropdown(
        data,
        "groupName",
        "groupCode"
      );

      handleInputChange("groupOptions", groupOptions);
    } catch (error) {
      console.error("Error loading Group data", error);
    } finally {
      handleInputChange("isloading", false);
    }
  };

  const handleDateChange = (newDate: dayjs.Dayjs | null) => {
    handleInputChange("selectedDate", newDate);
  };

  const handleReportDateChange = (newDate: dayjs.Dayjs | null) => {
    handleInputChange("reportSelectedDate", newDate);
  };

  const pathurl: string[] = [
    AppRoutes.WASTEREPORTBINOVERVIEW,
    AppRoutes.WASTEREPORTWASTECOLLECTION,
    AppRoutes.WASTEREPORTDICREPORT,
    AppRoutes.INFRAREPORTCORROSIONDETECTION,
    AppRoutes.INFRAREPORTCRACKDETECTION,
    AppRoutes.INFRAREPORTLEAKDETECTION,
    AppRoutes.AGRIREPORTANOMALYDETECTION,
    AppRoutes.AGRIREPORTGROWTHMONITORING,
    AppRoutes.AGRIREPORTPESTDETECTION,
    AppRoutes.AGRIREPORTDISEASEDETECTION,
    AppRoutes.RetailReportCustomerAnalysis,
    AppRoutes.RetailReportStocks,
  ];

  const isReportPath = pathurl.some((x) =>
    location.pathname.toLowerCase().includes(x.toLowerCase())
  );

  useEffect(() => {
    handleInputChange("reportSelectedDate", null);
  }, [location.pathname]);

  return (
    <Grid2
      container
      // spacing={2}
      sx={styles.headerContainer}
      alignItems="center"
    >
      {/* Breadcrumbs Section */}
      <Grid2 size={{ xs: 6 }} display={"flex"} alignItems={"center"}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/" onClick={(e) => e.preventDefault()}>
            <Home />
          </Link>

          {pathSegments.map((segment, index) => {
            const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
            const isLast = index === pathSegments.length - 1;

            return isLast ? (
              <Typography key={index} variant="caption" color="#838383">
                {segment.charAt(0).toUpperCase() + segment.slice(1)}
              </Typography>
            ) : (
              <Link key={index} color="inherit">
                {segment.charAt(0).toUpperCase() + segment.slice(1)}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Grid2>

      {/* Dropdowns Section */}
      <Grid2 size={{ xs: 6 }}>
        <Grid2 container spacing={2} justifyContent="flex-end">
          {masterState.selectedSiteCode.length > 0 && (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <Autocomplete
                disableClearable
                value={masterState.siteOptions.find(
                  (opt) => opt.value === masterState.selectedSiteCode
                )}
                onChange={(event, newValue) => {
                  handleInputChange(
                    "selectedSiteCode",
                    newValue ? newValue.value : ""
                  );
                  handleInputChange("groupOptions", []);
                  handleInputChange("selectedGroupCode", "");
                  const lat: number = parseFloat(newValue?.extraField1 ?? "0");
                  const lng: number = parseFloat(newValue?.extraField2 ?? "0");

                  handleInputChange("binPlottingmapCenter", {
                    lat,
                    lng,
                  });
                }}
                options={masterState.siteOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="siteCode"
                    label={t("global.selectSite")}
                    fullWidth
                    size="small"
                    margin="normal"
                    sx={styles.textField}
                  />
                )}
              />
            </Grid2>
          )}
          {/* Select Group */}

          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            {masterState.groupOptions.length > 0 && (
              <Autocomplete
                value={masterState?.groupOptions?.find(
                  (opt) => opt?.value === masterState?.selectedGroupCode
                )}
                onChange={(event, newValue) => {
                  handleInputChange(
                    "selectedGroupCode",
                    newValue ? newValue.value : ""
                  );
                }}
                options={masterState?.groupOptions ?? []}
                getOptionLabel={(option) => option.label ?? ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="groupCode"
                    label={t("global.selectGroup")}
                    fullWidth
                    size="small"
                    margin="normal"
                    sx={styles.textField}
                  />
                )}
              />
            )}
          </Grid2>

          {/* Date Picker */}
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={
                  isReportPath
                    ? masterState.reportSelectedDate
                    : masterState.selectedDate
                }
                format="DD/MM/YYYY" // Format to display date as dd/mm/yyyy
                onChange={
                  isReportPath ? handleReportDateChange : handleDateChange
                }
                disableFuture
                slotProps={{
                  textField: {
                    label: t("global.SelectDate"),
                    fullWidth: true,
                    margin: "normal",
                    size: "small",
                    sx: styles.textField,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid2>
        </Grid2>
      </Grid2>
      <Loading loading={masterState.isloading} type={`dots`} backDrop={true} />
    </Grid2>
  );
};

const styles = {
  headerContainer: {
    p: 1,
    justifyContent: "space-between",
    border: "1px solid #eee",
  },
  textField: {
    borderRadius: "5px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "5px",
    },
  },
};

export default HeaderComponent;
